import { createElement } from "react";

// Whitelist of formatting elements the backend message body may contain.
const ALLOWED_TAGS = new Set([
  "section",
  "h2",
  "h3",
  "p",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "br",
  "a",
]);
const SAFE_URL_SCHEMES = ["http:", "https:", "mailto:"];

function sanitizeHref(rawHref) {
  if (!rawHref) return null;
  try {
    const url = new URL(rawHref, window.location.origin);
    if (SAFE_URL_SCHEMES.includes(url.protocol)) {
      return url.href;
    }
  } catch {
    // Malformed URL -> treat as unsafe.
  }
  return null;
}

function convertNode(node, key) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tag = node.tagName.toLowerCase();

  // Drop any element that is not explicitly allowed, together with its
  // contents. This removes scripts, objects, forms, event handlers, etc.
  if (!ALLOWED_TAGS.has(tag)) {
    return null;
  }

  if (tag === "br") {
    return createElement("br", { key });
  }

  const children = Array.from(node.childNodes)
    .map((child, index) => convertNode(child, index))
    .filter((child) => child !== null && child !== undefined);

  const props = { key };

  if (tag === "a") {
    const href = sanitizeHref(node.getAttribute("href"));
    if (href) {
      props.href = href;
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }
  }

  return createElement(tag, props, children.length ? children : undefined);
}

/**
 * Parses an HTML string and returns sanitized React nodes containing only a
 * safe subset of formatting elements. No content is ever rendered through
 * `innerHTML` / `dangerouslySetInnerHTML`.
 */
export default function renderSafeHtml(html) {
  if (typeof html !== "string" || html.length === 0) {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  return Array.from(doc.body.childNodes)
    .map((node, index) => convertNode(node, index))
    .filter((child) => child !== null && child !== undefined);
}
